import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { searchProducts } from '../services/apiService';
import ReactDOM from 'react-dom';
import Pagination from './Pagination';
import { getArticleImageUrl } from '../utils/imageUtils';
import PriceDisplay from './PriceDisplay';

const ProductSearchModal = ({ open, onClose, searchTerm, onSelectProduct, onSelectProducts, multiSelect = false }) => {
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 30;
  const [selectedPharmacodes, setSelectedPharmacodes] = useState([]);

  // Clear selection when modal closes or search changes
  useEffect(() => {
    setSelectedPharmacodes([]);
  }, [open, searchTerm]);

  const toggleSelect = (pharmacode) => {
    setSelectedPharmacodes((prev) =>
      prev.includes(pharmacode)
        ? prev.filter((code) => code !== pharmacode)
        : [...prev, pharmacode]
    );
  };

  useEffect(() => {
    if (open && searchTerm && searchTerm.trim().length >= 2) {
      setIsLoading(true);
      setError(null);
      searchProducts(searchTerm, page, PAGE_SIZE)
        .then(response => {
          if (response?.success && Array.isArray(response.data.products)) {
            setResults(response.data.products);
            const pagination = response.data.pagination || {};
            setTotalPages(pagination.totalPages || 1);
            // Optionally sync page with backend (if backend can return a different page than requested)
            // setPage(pagination.currentPage || 1);
          } else {
            setResults([]);
            setTotalPages(1);
          }
        })
        .catch(() => { setError('Failed to fetch products'); setTotalPages(1); })
        .finally(() => setIsLoading(false));
    } else {
      setResults([]);
      setTotalPages(1);
    }
  }, [open, searchTerm, page]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, open]);

  const getOptimizedImageUrl = (images) => {
    if (!Array.isArray(images) || images.length === 0) return null;
    const picFront3D = images.find(image => image.includes('PICFRONT3D'));
    if (picFront3D) return picFront3D;
    const picFront = images.find(image => image.includes('PICFRONT'));
    if (picFront) return picFront;
    return images[0];
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.98, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98, y: 10 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative flex flex-col overflow-hidden bg-white shadow-2xl border border-gray-200 rounded-2xl w-full max-w-7xl max-h-[95vh] m-4"
          style={{ boxSizing: 'border-box' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200 bg-white sticky top-0 z-10 w-full">
            <h2 className="text-2xl font-bold text-gray-900">Select a Product</h2>
            <div className="flex items-center gap-4">
              {multiSelect && selectedPharmacodes.length > 0 && (
                <button
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-indigo-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg font-semibold text-base"
                  onClick={() => {
                    onSelectProducts?.(selectedPharmacodes);
                    setSelectedPharmacodes([]);
                    onClose();
                  }}
                >
                  Add Selected to Bestsellers ({selectedPharmacodes.length})
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          {/* Content */}
          <div className="flex-1 w-full h-full overflow-y-auto bg-gray-50">
            {isLoading ? (
              <div className="text-center py-10">
                <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-indigo-500 border-r-transparent"></div>
                <p className="mt-2 text-sm text-gray-500">Loading products...</p>
              </div>
            ) : error ? (
              <div className="text-center text-red-500 py-10">{error}</div>
            ) : results.length === 0 ? (
              <div className="text-center text-gray-500 py-10">No products found for "{searchTerm}"</div>
            ) : (
              <>
                <div className="grid gap-8 w-full h-full sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                  {results.map(product => {
                    const firstArticle = product.articles?.[0] || {};
                    const articleImages = Array.isArray(firstArticle.images)
                      ? { F: firstArticle.images, M: firstArticle.images }
                      : (firstArticle.images || (Array.isArray(product.images) ? { F: product.images } : (product.images || {})));
                    const article = {
                      pharmacode: firstArticle.pharmacode || product.pharmacode || product.productNumber,
                      description: {
                        description: firstArticle.description?.description || product.description?.description || 'No description available',
                        longDescription: firstArticle.description?.longDescription || product.description?.longDescription || product.longDescription || firstArticle.description?.description || 'No description available'
                      },
                      hasPhoto: firstArticle.hasPhoto ?? product.hasPhoto ?? false,
                      images: articleImages,
                      price: firstArticle.price ?? null
                    };
                    const isSelected = selectedPharmacodes.includes(article.pharmacode);
                    
                    return (
                      <div
                        key={product._id}
                        className={`flex flex-col items-center p-6 rounded-xl border border-gray-200 bg-white transition-all duration-200 shadow-sm h-full ${
                          multiSelect 
                            ? `cursor-pointer hover:bg-indigo-50 ${isSelected ? 'ring-2 ring-indigo-500 bg-indigo-50/30' : ''} hover:ring-2 hover:ring-indigo-300` 
                            : 'cursor-pointer hover:bg-indigo-50'
                        }`}
                        onClick={() => {
                          if (multiSelect) {
                            // In multi-select mode, clicking anywhere on the card selects it
                            toggleSelect(article.pharmacode);
                          } else {
                            // In single-select mode, select the product and close
                            onSelectProduct?.(product);
                            onClose();
                          }
                        }}
                      >
                        {multiSelect && (
                          <div className="w-full flex justify-end mb-2">
                            <div
                              className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center ${
                                isSelected 
                                  ? 'bg-indigo-500 border-indigo-500 text-white shadow-sm' 
                                  : 'bg-white/95 border-gray-300'
                              }`}
                            >
                              {isSelected && (
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="w-28 h-28 flex-shrink-0 rounded-lg overflow-hidden bg-white mb-4">
                          {(() => {
                            const imgUrl = getArticleImageUrl(article, 'F');
                            if (imgUrl) {
                              return (
                                <img
                                  src={imgUrl}
                                  alt={article.description.description}
                                  className="w-full h-full object-contain"
                                />
                              );
                            }
                            return (
                              <div className="w-full h-full flex items-center justify-center">
                                <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="flex-1 w-full flex flex-col items-center text-center">
                          <p className="text-base font-medium text-gray-900 line-clamp-2 mb-1">
                            {article.description.longDescription}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            Pharmacode: {article.pharmacode}
                          </p>
                          <PriceDisplay article={article} className="mt-1 text-indigo-600" />

                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    isLoading={isLoading}
                    showProductInfo={false}
                  />
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ProductSearchModal; 